import {computed, ref} from "vue";

/**
 * Replacement for deprecated `useResult()` function from Vue Apollo library.
 *
 * Extracts a value from the result object. When a result is available, a pick function will be used to extract
 * value from the result object. If no pick is given and the result has a single key, value of that key
 * will be used. If the result has multiple keys, entire result object will be returned.
 *
 * When the result is not available, default value will be returned.
 *
 * @template T
 * @param result Result ref as returned by the `useQuery` function.
 * @param defaultValue Value to use when the result is not available.
 * @param {(data: any) => Any} pick Function that will be used to extract the result value.
 * @return {Any} Extracted value or default value.
 */
export function useResult(result, defaultValue = undefined, pick = null) {
    return computed(() => {
        const value = result.value;
        if (value) {
            if (pick) {
                try {
                    return pick(value);
                } catch (e) {
                    console.warn('An error occurred when calling pick for a result: %o', e);
                }
            } else {
                const keys = Object.keys(value);
                if (keys.length === 1) {
                    // Return the single key from result.
                    return value[keys[0]];
                } else {
                    // Return entire result data
                    return value;
                }
            }
        }

        return defaultValue;
    });
}

/**
 * Modification of {@link useResult} that remembers a value from the last available result. The behaviour is the same,
 * except when the result becomes unavailable, value from the last result will be used.
 *
 * @see useResult
 *
 * @template T
 * @param result Result ref as returned by the `useQuery` function.
 * @param defaultValue Value to use when the result is not available.
 * @param {(data: T) => Any} pick Function that will be used to extract the result value.
 * @return {Any} Extracted value or default value.
 */
export function useLastResult(result, defaultValue = undefined, pick = null) {
    const lastValue = ref();

    return computed(() => {
        const value = result.value;
        if (value) {
            let resultValue;

            if (pick) {
                try {
                    resultValue = pick(value);
                } catch (e) {
                    console.warn('An error occurred when calling pick for a result: %o', e);
                    return defaultValue;
                }
            } else {
                const keys = Object.keys(value);
                if (keys.length === 1) {
                    // Return the single key from result.
                    resultValue = value[keys[0]];
                } else {
                    // Return entire result data
                    resultValue = value;
                }
            }

            lastValue.value = resultValue;
            return resultValue;
        } else if (lastValue.value !== undefined) {
            return lastValue.value;
        }

        return defaultValue;
    });
}
